// React
import { memo, useCallback } from "react";

// Servisni sloj
import { formatUnitPrice } from "services/api/pricing/consts";

// UI
import { Badge } from "ui/components/Badge";
import { Button } from "ui/components/Button/Button";
import { TableSearch } from "ui/components/Table/TableSearch";

// Tipovi i konstante
import { SCHEME_ITEM_COLUMNS } from "./consts";
import { PricingSchemeCardProps } from "../../types";

/**
 * Jedna cjenovna shema.
 *
 * Neaktivna shema se ne nudi novim klijentima, ali oni koji su već na njoj nastavljaju po njoj —
 * nitko ne poskupi bez odluke čovjeka.
 */
const PricingSchemeCard: React.FC<PricingSchemeCardProps> = (props: PricingSchemeCardProps) => {
  const { scheme, impact, onEdit, onShowImpact } = props;

  const handleEditClick = useCallback(() => {
    onEdit(scheme);
  }, [onEdit, scheme]);

  const handleImpactClick = useCallback(() => {
    onShowImpact(scheme);
  }, [onShowImpact, scheme]);

  return (
    <div className="p-6">
      {/* Naziv i radnje u zaglavlju grupe: gumbi su isti za svaku, pa stoje na istom mjestu. */}
      <div className="flex items-start justify-between gap-4 mb-4">
        {/* Naziv i opis idu u zaglavlje da kartica i tablica ispod krenu od iste crte. */}
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-base font-bold text-slate-600">{scheme.name}</p>

            {!scheme.isActive && (
              <Badge text="Neaktivna" variant="secondary" height="small" showDot />
            )}

            {/* Uz naziv, ne u stupcu: ondje bi pomicao karticu i razbio poravnanje s tablicom. */}
            {!scheme.isActive && scheme.tenantCount > 0 && (
              <Badge
                text="Zaostali u neaktivnoj grupi"
                variant="warning"
                height="small"
                showDot
              />
            )}
          </div>

          {scheme.description && (
            <p className="text-sm text-slate-500 mt-1">{scheme.description}</p>
          )}
        </div>

        <div className="flex items-center gap-2">
          <Button variant="outlined" children="Uredi" onClick={handleEditClick} />

          {/*
            Modal nosi popis pogođenih klijenata; brojke stoje uz tablicu. Bez ijednog klijenta
            u njemu nema ničega, pa se gumb tada ni ne nudi.
          */}
          {scheme.tenantCount > 0 && (
            <Button variant="outlined" children="Klijenti" onClick={handleImpactClick} />
          )}
        </div>
      </div>

      <div className="flex flex-col xl:flex-row gap-6 xl:gap-16">
        <div className="w-full xl:w-[320px] xl:flex-shrink-0">
          {/*
            Zaglavlje stupca, iste visine i tipografije kao zaglavlje tablice (`h-[48px]` u
            `TableHeader`), da bijeli okvir ispod krene od iste crte kao i tablica.
          */}
          <div className="h-[48px] flex items-center px-4 text-xs font-normal text-slate-900 uppercase">
            Učinak grupe
          </div>

          {/*
            Učinak je podatak o grupi, a tablica desno govori o stavkama. Modal iza gumba
            ostaje za popis klijenata, koji ovdje ne bi stao.
          */}
          <div className="rounded-sm border border-slate-200 bg-white p-4">
            {impact === null ? (
              <p className="text-sm text-slate-400">Učitavanje…</p>
            ) : (
              <div className="flex flex-row gap-8">
                <div>
                  <p className="text-xs text-slate-500">Klijenata</p>
                  <p className="text-2xl font-bold text-slate-700">{impact.tenantCount}</p>
                </div>

                <div>
                  <p className="text-xs text-slate-500">Mjesečno</p>
                  <p className="text-2xl font-bold text-slate-700">
                    {formatUnitPrice.format(impact.monthlyEffect)}
                  </p>

                  {impact.unmeasured.length > 0 && (
                    <p className="text-xs text-amber-700 mt-1">
                      Bez {impact.unmeasured.length} ključa kojima se količina ne zna.
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="flex-1">
          {scheme.items.length === 0 ? (
            <p className="text-sm text-slate-400 py-3">
              Bez ijedne stavke. Klijent plaća standardne cijene iz cjenika.
            </p>
          ) : (
            <TableSearch
              columns={SCHEME_ITEM_COLUMNS}
              data={scheme.items}
              selectableRows={false}
              disablePagination
            />
          )}
        </div>

      </div>
    </div>
  );
};

export default memo(PricingSchemeCard);
